import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { NewStatisticModal } from '../components/NewStatisticModal';
import { normalizeStatisticCategory } from '@shared/statistic-category';
import { normalizeStatisticSegment } from '@shared/statistic-segment';
import { isDetailedAnalysisVisible } from '@shared/statistic-data';
import { normalizeRelatedLinks } from '@shared/related-links';
import { 
  Plus, 
  FileText, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Globe, 
  Calendar,
  Tag,
  BarChart3,
  Settings,
  Search,
  Filter,
  X,
  Sparkles,
  Star,
  RefreshCw,
  User
} from 'lucide-react';

interface Statistic {
  id: string;
  title: string;
  description: string;
  location: string;
  language: string;
  status: 'draft' | 'pending' | 'published';
  type?: 'statistic' | 'curioseme';
  category?: 'politics' | 'society' | 'economy' | 'culture' | 'entertainment' | 'sports' | 'technology' | 'science' | 'lifestyle' | 'unclassified';
  featured?: boolean;
  data?: {
    showDetailedAnalysis?: boolean;
    blocks?: unknown[];
    segments?: unknown[];
    charts?: unknown[];
    evolution?: unknown;
  };
  author: string;
  authorId?: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  slug: string;
  imageUrl?: string;
}

interface AuthorOption {
  id: string;
  name: string;
  isDefault: boolean;
}

const Statistics: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<Statistic[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [clearingCache, setClearingCache] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Modal state for new statistic with AI - Multi-step process
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [topic, setTopic] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [generatedData, setGeneratedData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Multi-step process state
  const [currentStep, setCurrentStep] = useState<'topic' | 'blocks' | 'segments' | 'info' | 'preview'>('topic');
  const [blocksData, setBlocksData] = useState<any[]>([]);
  const [segmentsData, setSegmentsData] = useState<any[]>([]);
  const [infoData, setInfoData] = useState<any>(null);
  
  // Content type state
  const [contentType, setContentType] = useState<'statistic' | 'curioseme'>('statistic');
  
  // Curioseme modal state
  const [showCuriosemeModal, setShowCuriosemeModal] = useState(false);
  
  // New Statistic (AI) modal state
  const [showNewStatisticModal, setShowNewStatisticModal] = useState(false);
  const [curiosemeTopic, setCuriosemeTopic] = useState('');
  const [relatedStatistics, setRelatedStatistics] = useState<any[]>([]);
  const [availableStatistics, setAvailableStatistics] = useState<any[]>([]);
  const [isGeneratingCurioseme, setIsGeneratingCurioseme] = useState(false);
  const [generatedCurioseme, setGeneratedCurioseme] = useState<any>(null);

  
  // Block editing state
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingBlockData, setEditingBlockData] = useState<any>(null);
  
  // Segment editing state
  const [editingSegmentIndex, setEditingSegmentIndex] = useState<number | null>(null);
  const [editingSegmentData, setEditingSegmentData] = useState<any>(null);

  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [isImportedData, setIsImportedData] = useState(false);

  // Featured modal state
  const [showFeaturedModal, setShowFeaturedModal] = useState(false);
  const [featuredStatistic, setFeaturedStatistic] = useState<Statistic | null>(null);
  const [currentFeaturedStatistic, setCurrentFeaturedStatistic] = useState<Statistic | null>(null);
  const [togglingDetailedAnalysis, setTogglingDetailedAnalysis] = useState<string | null>(null);
  const [authors, setAuthors] = useState<AuthorOption[]>([]);
  const [updatingAuthorId, setUpdatingAuthorId] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    fetchStatistics();
    fetchAuthors();
  }, [user, navigate]);

  const fetchAuthors = async () => {
    try {
      const response = await fetch('/api/authors', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAuthors(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('/api/statistics', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      } else {
        console.error('Failed to fetch statistics');
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  const handleCreateCurioseme = async () => {
    setShowCuriosemeModal(true);
    // Fetch available statistics for selection
    try {
      const response = await fetch('/api/curioseme-statistics', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableStatistics(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching statistics for curioseme:', error);
    }
  };

  const handleGenerateCurioseme = async () => {
    if (!curiosemeTopic.trim()) {
      alert('Por favor, introduce un tema para el curioseme');
      return;
    }

    setIsGeneratingCurioseme(true);
    try {
      const response = await fetch('/api/curiosemes/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          topic: curiosemeTopic,
          relatedStatistics: relatedStatistics.map(stat => ({
            id: stat.id,
            location: stat.location,
            title: stat.title,
            lead: stat.lead,
            description: stat.description,
            studyFlow: stat.studyFlow,
            slug: stat.slug,
            blocks: stat.data.blocks
          }))
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setGeneratedCurioseme(result.data);
        setShowPreview(true);
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error generating curioseme:', error);
      alert('Error al generar el curioseme');
    } finally {
      setIsGeneratingCurioseme(false);
    }
  };

  const handleSaveCurioseme = async () => {
    if (!generatedCurioseme) return;

    try {
      const response = await fetch('/api/statistics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...generatedCurioseme,
          type: 'curioseme'
        }),
      });

      if (response.ok) {
        const savedCurioseme = await response.json();
        setStatistics(prev => [savedCurioseme, ...prev]);
        setShowCuriosemeModal(false);
        setShowPreview(false);
        setCuriosemeTopic('');
        setRelatedStatistics([]);
        setGeneratedCurioseme(null);
        alert('Curioseme creado exitosamente');
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error saving curioseme:', error);
      alert('Error al guardar el curioseme');
    }
  };

  const handleAddRelatedStatistic = (statistic: any) => {
    if (relatedStatistics.length >= 2) {
      alert('Solo puedes seleccionar hasta 2 estadísticas relacionadas');
      return;
    }
    setRelatedStatistics(prev => [...prev, statistic]);
  };

  const handleRemoveRelatedStatistic = (index: number) => {
    setRelatedStatistics(prev => prev.filter((_, i) => i !== index));
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setTopic('');
    setGeneratedData(null);
    setShowPreview(false);
    setCurrentStep('topic');
    setBlocksData([]);
    setSegmentsData([]);
    setInfoData(null);
    setEditingBlockIndex(null);
    setEditingBlockData(null);
    setEditingSegmentIndex(null);
    setEditingSegmentData(null);
    setShowImportModal(false);
    setImportFile(null);
    setImportError('');
    setIsImportedData(false);
  };

  const handleCloseCuriosemeModal = () => {
    setShowCuriosemeModal(false);
    setCuriosemeTopic('');
    setRelatedStatistics([]);
    setGeneratedCurioseme(null);
    setShowPreview(false);
  };

  const handleCreateFromPreview = async () => {
    if (!generatedData) return;
    
    setIsCreating(true);
    
    try {
      // console.log('🔍 DEBUG: Creating statistic from preview:', generatedData); // Removed for production
      
      const createResponse = await fetch('/api/statistics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...generatedData,
          status: 'draft' // Always create as draft first
        }),
      });

      if (createResponse.ok) {
        const newStatistic = await createResponse.json();
        // console.log('🔍 DEBUG: Created statistic:', newStatistic); // Removed for production
        
        // Refresh the statistics list
        await fetchStatistics();
        
        // Show success message
        alert(`¡Estadística creada exitosamente!\n\nTítulo: ${generatedData.title}\n\nLa estadística se ha guardado como borrador. Puedes editarla desde la lista.`);
        
        handleCloseModal();
      } else {
        const createError = await createResponse.json();
        throw new Error(createError.message || 'Error al guardar la estadística');
      }
    } catch (error) {
      console.error('Error creating statistic from preview:', error);
      alert(`Error al crear la estadística: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Step 1: Generate blocks
  const handleGenerateBlocks = async () => {
    if (!topic.trim()) {
      alert('Por favor, introduce un tema para la estadística');
      return;
    }

    setIsCreating(true);
    
    try {
      // console.log('🔍 DEBUG: Generating blocks with AI assistant:', topic); // Removed for production
      
      const response = await fetch('/api/statistics/generate-blocks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          topic: topic.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar bloques con IA');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // console.log('🔍 DEBUG: Blocks assistant response:', result.data); // Removed for production
        
        // Store blocks data and move to next step
        setBlocksData(result.data.blocks || []);
        setCurrentStep('blocks');
      } else {
        throw new Error(result.error || 'Error en la respuesta del asistente de bloques');
      }
      
    } catch (error) {
      console.error('Error generating blocks with AI:', error);
      alert(`Error al generar bloques con IA: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Step 2: Generate segments
  const handleGenerateSegments = async () => {
    setIsCreating(true);
    
    try {
      // console.log('🔍 DEBUG: Generating segments with AI assistant:', topic); // Removed for production
      
      const response = await fetch('/api/statistics/generate-segments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          topic: topic.trim(),
          blocks: blocksData
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar segmentos con IA');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // console.log('🔍 DEBUG: Segments assistant response:', result.data); // Removed for production
        
        // Store segments data and move to next step
        setSegmentsData(result.data.segments || []);
        setCurrentStep('segments');
      } else {
        throw new Error(result.error || 'Error en la respuesta del asistente de segmentos');
      }
      
    } catch (error) {
      console.error('Error generating segments with AI:', error);
      alert(`Error al generar segmentos con IA: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Step 3: Generate info
  const handleGenerateInfo = async () => {
    setIsCreating(true);
    
    try {
      // console.log('🔍 DEBUG: Generating info with AI assistant:', topic); // Removed for production
      
      const response = await fetch('/api/statistics/generate-info', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          topic: topic.trim(),
          data: {
            blocks: blocksData,
            segments: segmentsData
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al generar información con IA');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // console.log('🔍 DEBUG: Info assistant response:', result.data); // Removed for production
        
        // Store info data and move to preview
        setInfoData(result.data);
        
        // Combine all data for final preview
        const defaultAuthor = authors.find((a) => a.isDefault) || authors[0];
        const finalData = {
          ...result.data,
          author: defaultAuthor?.name || result.data.author || 'Equipo de Statemia',
          authorId: defaultAuthor?.id,
          data: {
            blocks: blocksData,
            segments: segmentsData
          }
        };
        
        setGeneratedData(finalData);
        setCurrentStep('preview');
        setShowPreview(true);
      } else {
        throw new Error(result.error || 'Error en la respuesta del asistente de información');
      }
      
    } catch (error) {
      console.error('Error generating info with AI:', error);
      alert(`Error al generar información con IA: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle blocks data updates
  const handleBlocksUpdate = (updatedBlocks: any[]) => {
    setBlocksData(updatedBlocks);
  };

  // Handle segments data updates
  const handleSegmentsUpdate = (updatedSegments: any[]) => {
    setSegmentsData(updatedSegments);
  };

  // Handle block editing
  const handleEditBlock = (index: number) => {
    setEditingBlockIndex(index);
    setEditingBlockData({ ...blocksData[index] });
  };

  const handleSaveBlock = () => {
    if (editingBlockIndex !== null && editingBlockData) {
      const updatedBlocks = [...blocksData];
      updatedBlocks[editingBlockIndex] = editingBlockData;
      setBlocksData(updatedBlocks);
      setEditingBlockIndex(null);
      setEditingBlockData(null);
    }
  };

  const handleCancelEditBlock = () => {
    setEditingBlockIndex(null);
    setEditingBlockData(null);
  };

  const handleUpdateBlockField = (field: string, value: any) => {
    if (editingBlockData) {
      setEditingBlockData({
        ...editingBlockData,
        [field]: value
      });
    }
  };

  // Handle segment editing
  const handleEditSegment = (index: number) => {
    setEditingSegmentIndex(index);
    setEditingSegmentData({ ...segmentsData[index] });
  };

  const handleSaveSegment = () => {
    if (editingSegmentIndex !== null && editingSegmentData) {
      const updatedSegments = [...segmentsData];
      updatedSegments[editingSegmentIndex] = editingSegmentData;
      setSegmentsData(updatedSegments);
      setEditingSegmentIndex(null);
      setEditingSegmentData(null);
    }
  };

  const handleCancelEditSegment = () => {
    setEditingSegmentIndex(null);
    setEditingSegmentData(null);
  };

  const handleUpdateSegmentField = (field: string, value: any) => {
    if (editingSegmentData) {
      setEditingSegmentData({
        ...editingSegmentData,
        [field]: value
      });
    }
  };

  const handleUpdateSegmentValue = (key: string, value: number) => {
    if (editingSegmentData) {
      setEditingSegmentData({
        ...editingSegmentData,
        values: {
          ...editingSegmentData.values,
          [key]: value
        }
      });
    }
  };

  const handleView = (slug: string) => {
    navigate(`/statistics/${slug}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/statistics/edit/${id}`);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta estadística?')) {
      return;
    }

    try {
      const response = await fetch(`/api/statistics/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setStatistics(statistics.filter(stat => stat.id !== id));
      } else {
        console.error('Failed to delete statistic');
      }
    } catch (error) {
      console.error('Error deleting statistic:', error);
    }
  };

  const handleClearCache = async (statisticId: string, statisticTitle: string) => {
    setClearingCache(statisticId);
    
    try {
      const response = await fetch(`/api/statistics/${statisticId}/clear-cache`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to clear cache');
      }

      const result = await response.json();
      alert(`Cache limpiado para: ${result.statisticTitle}`);
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('Error al limpiar el cache');
    } finally {
      setClearingCache(null);
    }
  };

  const handleClearLandingCache = async () => {
    try {
      const response = await fetch('/api/admin/clear-landing-cache', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to clear landing cache');
      }

      const result = await response.json();
      alert(`✅ ALL cache cleared successfully!\n\nCleared: ${result.totalCleared} cache entries\nIncludes: Landing + Articles + Assets`);
      // console.log('🗑️ ALL CACHE CLEARED:', result); // Removed for production
    } catch (error) {
      console.error('Error clearing landing cache:', error);
      alert('❌ Error al limpiar el cache de la landing page');
    }
  };

  const handleStatusChange = async (id: string, newStatus: 'draft' | 'pending' | 'published') => {
    try {
      const response = await fetch(`/api/statistics/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setStatistics(statistics.map(stat => 
          stat.id === id ? { ...stat, status: newStatus } : stat
        ));
        await queryClient.invalidateQueries({ queryKey: ['/api/landing/complete'] });
      } else {
        console.error('Failed to update statistic status');
      }
    } catch (error) {
      console.error('Error updating statistic status:', error);
    }
  };

  const handleAuthorChange = async (id: string, authorId: string) => {
    const selected = authors.find((a) => a.id === authorId);
    if (!selected) return;

    setUpdatingAuthorId(id);
    try {
      const response = await fetch(`/api/statistics/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ authorId: selected.id, author: selected.name }),
      });

      if (response.ok) {
        setStatistics((prev) =>
          prev.map((stat) =>
            stat.id === id
              ? { ...stat, authorId: selected.id, author: selected.name }
              : stat
          )
        );
      } else {
        console.error('Failed to update author');
        alert('No se pudo actualizar el autor');
      }
    } catch (error) {
      console.error('Error updating author:', error);
      alert('Error al actualizar el autor');
    } finally {
      setUpdatingAuthorId(null);
    }
  };

  // Function to get Spanish display name from English category value
  const getCategoryDisplayName = (englishCategory: string): string => {
    const categoryMap: { [key: string]: string } = {
      'politics': 'Política',
      'society': 'Sociedad',
      'economy': 'Economía',
      'culture': 'Cultura',
      'entertainment': 'Entretenimiento',
      'sports': 'Deportes',
      'technology': 'Tecnología',
      'science': 'Ciencia',
      'lifestyle': 'Lifestyle',
      'unclassified': 'Inclasificables'
    };
    
    return categoryMap[englishCategory] || englishCategory;
  };

  const handleCategoryChange = async (id: string, newCategory: string) => {
    try {
      const categoryValue = newCategory || undefined;
      const response = await fetch(`/api/statistics/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ category: categoryValue }),
      });

      if (response.ok) {
        setStatistics(statistics.map(stat => 
          stat.id === id ? { ...stat, category: categoryValue as any } : stat
        ));
      } else {
        console.error('Failed to update statistic category');
      }
    } catch (error) {
      console.error('Error updating statistic category:', error);
    }
  };

  const handleFeaturedChange = async (statistic: Statistic) => {
    const currentFeatured = statistics.find(stat => stat.featured === true);

    if (currentFeatured && currentFeatured.id === statistic.id) {
      await updateFeaturedStatus(statistic.id, false);
    } else {
      setCurrentFeaturedStatistic(currentFeatured || null);
      setFeaturedStatistic(statistic);
      setShowFeaturedModal(true);
    }
  };

  const updateFeaturedStatus = async (id: string, featured: boolean) => {
    try {
      // console.log('🔍 DEBUG: Updating featured status for ID:', id, 'to:', featured); // Removed for production
      
      if (featured) {
        // If we're setting a new featured article, first unfeature all others
        // console.log('🔍 DEBUG: Unfeaturing all other articles first...'); // Removed for production
        
        // Find current featured article
        const currentFeatured = statistics.find(stat => stat.featured === true);
        
        if (currentFeatured && currentFeatured.id !== id) {
          // console.log('🔍 DEBUG: Unfeaturing current featured article:', currentFeatured.id); // Removed for production
          
          // Unfeature the current featured article
          const unfeatureResponse = await fetch(`/api/statistics/${currentFeatured.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ featured: false }),
          });
          
          if (!unfeatureResponse.ok) {
            console.error('Failed to unfeature current featured article');
            return;
          }
          
          // console.log('🔍 DEBUG: Successfully unfeatured current featured article'); // Removed for production
        }
      }
      
      // Now update the target article
      const response = await fetch(`/api/statistics/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ featured }),
      });

      // console.log('🔍 DEBUG: Response status:', response.status); // Removed for production
      // console.log('🔍 DEBUG: Response ok:', response.ok); // Removed for production

      if (response.ok) {
        const updatedStatistic = await response.json();
        // console.log('🔍 DEBUG: Updated statistic from server:', updatedStatistic); // Removed for production
        
        // Update local state - ensure only one is featured
        setStatistics(prev => prev.map(stat => ({
          ...stat,
          featured: stat.id === id ? featured : false
        })));
        
        // console.log('🔍 DEBUG: Local state updated'); // Removed for production
        
        // Close modal
        setShowFeaturedModal(false);
        setFeaturedStatistic(null);
        setCurrentFeaturedStatistic(null);
      } else {
        const errorText = await response.text();
        console.error('Failed to update featured status:', errorText);
      }
    } catch (error) {
        console.error('Error updating featured status:', error);
    }
  };

  const handleDetailedAnalysisToggle = async (statistic: Statistic) => {
    setTogglingDetailedAnalysis(statistic.id);
    try {
      let currentData = statistic.data;
      if (!currentData?.blocks) {
        const fetchResponse = await fetch(`/api/statistics/${statistic.id}`, {
          credentials: 'include',
        });
        if (!fetchResponse.ok) {
          throw new Error('No se pudo cargar la estadística');
        }
        const fullStatistic = await fetchResponse.json();
        currentData = fullStatistic.data;
      }

      const nextVisible = !isDetailedAnalysisVisible(currentData);
      const response = await fetch(`/api/statistics/${statistic.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          data: {
            ...currentData,
            showDetailedAnalysis: nextVisible,
          },
        }),
      });

      if (response.ok) {
        setStatistics(prev =>
          prev.map(stat =>
            stat.id === statistic.id
              ? {
                  ...stat,
                  data: {
                    ...stat.data,
                    showDetailedAnalysis: nextVisible,
                  },
                }
              : stat
          )
        );
      } else {
        console.error('Failed to update detailed analysis visibility');
      }
    } catch (error) {
      console.error('Error updating detailed analysis visibility:', error);
    } finally {
      setTogglingDetailedAnalysis(null);
    }
  };

  const handleImportStatistics = async () => {
    if (!importFile) {
      setImportError('Por favor, selecciona un archivo JSON');
      return;
    }

    setIsImporting(true);
    setImportError('');

    try {
      // Read the file as text
      const fileText = await importFile.text();
      
      // Parse JSON
      let jsonData;
      try {
        jsonData = JSON.parse(fileText);
      } catch (parseError) {
        throw new Error('El archivo no contiene JSON válido');
      }

      // Support both single statistic object and array of statistics
      const statisticsArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      
      if (statisticsArray.length === 0) {
        throw new Error('El archivo no contiene estadísticas válidas');
      }

      // For now, we'll import the first statistic and show in preview
      const firstStatistic = statisticsArray[0];
      const importAuthorName = firstStatistic.author || 'Equipo de Statemia';
      const matchedAuthor =
        authors.find((a) => a.name.toLowerCase() === String(importAuthorName).toLowerCase()) ||
        authors.find((a) => a.isDefault) ||
        authors[0];
      
      // Map JSON fields to the expected format (supports snake_case and camelCase)
      const mappedData = {
        title: firstStatistic.title || '',
        lead: firstStatistic.lead || '',
        description: firstStatistic.description || '',
        studyFlow: firstStatistic.study_flow || firstStatistic.studyFlow || '',
        location: firstStatistic.location || 'Spain',
        language: firstStatistic.language || 'es',
        author: matchedAuthor?.name || importAuthorName,
        authorId: matchedAuthor?.id || firstStatistic.authorId || firstStatistic.author_id || undefined,
        tags: firstStatistic.tags || [],
        category: normalizeStatisticCategory(firstStatistic.category),
        conclusion: firstStatistic.conclusion || '',
        highlights: firstStatistic.highlights || [],
        radar: firstStatistic.radar || '',
        metaTitle: firstStatistic.meta_title || firstStatistic.metaTitle || '',
        metaDescription: firstStatistic.meta_description || firstStatistic.metaDescription || '',
        keywords: firstStatistic.keywords || [],
        canonicalUrl: firstStatistic.canonical_url || firstStatistic.canonicalUrl || '',
        ogImage: firstStatistic.og_image || firstStatistic.ogImage || '',
        ogImageAlt: firstStatistic.og_image_alt || firstStatistic.ogImageAlt || '',
        relatedLinks: normalizeRelatedLinks(
          firstStatistic.related_links || firstStatistic.relatedLinks || []
        ),
        relatedAdProducts: firstStatistic.related_ad_products || firstStatistic.relatedAdProducts || [],
        imageId: firstStatistic.image_id || firstStatistic.imageId || undefined,
        type: firstStatistic.type || 'statistic',
        data: {
          blocks: firstStatistic.data?.blocks || [],
          segments: (firstStatistic.data?.segments || []).map((segment: unknown, index: number) =>
            normalizeStatisticSegment(segment, index)
          ),
          evolution: firstStatistic.data?.evolution,
          charts: firstStatistic.data?.charts,
        },
      };

      if (!mappedData.studyFlow) {
        throw new Error('El JSON debe incluir studyFlow o study_flow');
      }
      if (!mappedData.data?.blocks?.length) {
        throw new Error('El JSON debe incluir data.blocks con al menos un bloque');
      }

      // Set the imported data and show preview
      setGeneratedData(mappedData);
      setCurrentStep('preview');
      setIsImportedData(true);
      setShowImportModal(false);
      setShowCreateModal(true);
      
      // Reset import state
      setImportFile(null);

    } catch (error) {
      console.error('Error importing statistics:', error);
      setImportError(error instanceof Error ? error.message : 'Error desconocido al importar');
    } finally {
      setIsImporting(false);
    }
  };

  const filteredStatistics = statistics.filter(stat => {
    const matchesSearch = stat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         stat.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         stat.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || stat.status === statusFilter;
    const matchesLocation = locationFilter === 'all' || stat.location === locationFilter;
    const matchesType = typeFilter === 'all' || stat.type === typeFilter;
    const matchesCategory = categoryFilter === 'all' || stat.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesLocation && matchesType && matchesCategory;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-500 text-white';
      case 'pending': return 'bg-yellow-500 text-white';
      case 'published': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <FileText size={16} />;
      case 'pending': return <Eye size={16} />;
      case 'published': return <EyeOff size={16} />;
      default: return <FileText size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="pt-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="statemia-card">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="statemia-card mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Estadísticas
            </h1>
            <p className="text-muted-foreground">
              Gestiona las estadísticas de Statemia
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCreateNew}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              Nueva Estadística
            </button>
            {/* Commented out - Nueva Curioseme button (keeping for future reference)
            <button
              onClick={handleCreateCurioseme}
              className="btn-secondary flex items-center gap-2"
            >
              <Sparkles size={20} />
              Nueva Curioseme
            </button>
            */}
            <button
              onClick={() => setShowNewStatisticModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <BarChart3 size={20} />
              Nueva Estadística (NEW)
            </button>
            <button
              onClick={handleClearLandingCache}
              className="btn-outline flex items-center gap-2 text-orange-600 border-orange-200 hover:bg-orange-50"
              title="Limpiar TODO el cache (Landing + Artículos + Assets)"
            >
              <RefreshCw size={20} />
              Limpiar TODO Cache
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <FileText size={20} />
              Importar
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="statemia-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              placeholder="Buscar estadísticas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="pending">Pendiente</option>
            <option value="published">Publicado</option>
          </select>

          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todas las ubicaciones</option>
            <option value="Spain">España</option>
            <option value="ww">Mundial</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todos los tipos</option>
            <option value="statistic">Estadísticas</option>
            <option value="curioseme">Curiosemes</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todas las categorías</option>
            <option value="politics">Política</option>
            <option value="society">Sociedad</option>
            <option value="economy">Economía</option>
            <option value="culture">Cultura</option>
            <option value="entertainment">Entretenimiento</option>
            <option value="sports">Deportes</option>
            <option value="technology">Tecnología</option>
            <option value="science">Ciencia</option>
            <option value="energy">Energía</option>
            <option value="lifestyle">Lifestyle</option>
            <option value="unclassified">Inclasificables</option>
          </select>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter size={16} />
            {filteredStatistics.length} de {statistics.length} estadísticas
          </div>
        </div>
      </div>

      {/* Statistics List */}
      <div className="space-y-4">
        {filteredStatistics.length === 0 ? (
          <div className="statemia-card">
            <div className="text-center py-12">
              <BarChart3 size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No hay estadísticas
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter !== 'all' || locationFilter !== 'all' 
                  ? 'No se encontraron estadísticas con los filtros aplicados'
                  : 'Crea tu primera estadística para comenzar'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && locationFilter === 'all' && (
                <button
                  onClick={handleCreateNew}
                  className="btn-primary"
                >
                  Crear Primera Estadística
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredStatistics.map((statistic) => (
            <div key={statistic.id} className="statemia-card">
              <div className="flex gap-4">
                {/* Image */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-surface-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {statistic.imageUrl ? (
                      <img 
                        src={statistic.imageUrl} 
                        alt={statistic.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground" style={{ display: statistic.imageUrl ? 'none' : 'flex' }}>
                      <BarChart3 size={32} />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-foreground truncate">
                          {statistic.title}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${getStatusColor(statistic.status)}`}>
                          {getStatusIcon(statistic.status)}
                          {statistic.status === 'draft' && 'Borrador'}
                          {statistic.status === 'pending' && 'Pendiente'}
                          {statistic.status === 'published' && 'Publicado'}
                        </span>
                        {statistic.type && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${
                            statistic.type === 'curioseme' 
                              ? 'bg-orange-100 text-orange-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {statistic.type === 'curioseme' ? <Sparkles size={12} /> : <BarChart3 size={12} />}
                            {statistic.type === 'curioseme' ? 'Curioseme' : 'Estadística'}
                          </span>
                        )}
                        {!isDetailedAnalysisVisible(statistic.data) && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 bg-slate-100 text-slate-600">
                            <EyeOff size={12} />
                            Sin análisis detallado
                          </span>
                        )}
                      </div>
                      
                      <p className="text-muted-foreground mb-3 line-clamp-2">
                        {statistic.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        <div className="flex items-center gap-1">
                          <Globe size={16} />
                          {statistic.location}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar size={16} />
                          {new Date(statistic.createdAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Tag size={16} />
                          {statistic.language.toUpperCase()}
                        </div>
                        {statistic.category && (
                          <div className="flex items-center gap-1">
                            <Tag size={16} />
                            <span className="capitalize">{getCategoryDisplayName(statistic.category)}</span>
                          </div>
                        )}
                      </div>
                      
                      {statistic.tags && statistic.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {statistic.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-surface-200 text-surface-600 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {statistic.tags.length > 3 && (
                            <span className="px-2 py-1 bg-surface-200 text-surface-600 rounded-full text-xs">
                              +{statistic.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 text-sm">
                        <User size={14} className="text-muted-foreground flex-shrink-0" />
                        <select
                          value={statistic.authorId || ''}
                          disabled={updatingAuthorId === statistic.id || authors.length === 0}
                          onChange={(e) => handleAuthorChange(statistic.id, e.target.value)}
                          className="text-sm bg-transparent border border-surface-300 rounded-lg px-2 py-1 cursor-pointer hover:bg-surface-200 transition-colors max-w-[220px]"
                          title="Cambiar autor de la noticia"
                        >
                          {!statistic.authorId && (
                            <option value="">{statistic.author || 'Sin autor'}</option>
                          )}
                          {authors.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}{a.isDefault ? ' (por defecto)' : ''}
                            </option>
                          ))}
                        </select>
                        {updatingAuthorId === statistic.id && (
                          <RefreshCw size={14} className="animate-spin text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleView(statistic.slug)}
                    className="btn-secondary flex items-center gap-2 px-3 py-2"
                    title="Ver"
                  >
                    <Eye size={16} />
                    Ver
                  </button>
                  
                  <button
                    onClick={() => handleEdit(statistic.id)}
                    className="btn-secondary flex items-center gap-2 px-3 py-2"
                    title="Editar"
                  >
                    <Edit size={16} />
                    Editar
                  </button>
                  
                  <div className="relative">
                    <select
                      value={statistic.status}
                      onChange={(e) => handleStatusChange(statistic.id, e.target.value as 'draft' | 'pending' | 'published')}
                      className="p-2 text-sm bg-transparent border border-surface-300 rounded-lg cursor-pointer hover:bg-surface-200 transition-colors"
                    >
                      <option value="draft">Borrador</option>
                      <option value="pending">Pendiente</option>
                      <option value="published">Publicado</option>
                    </select>
                  </div>
                  
                  <div className="relative">
                    <select
                      value={statistic.category || ''}
                      onChange={(e) => handleCategoryChange(statistic.id, e.target.value)}
                      className="p-2 text-sm bg-transparent border border-surface-300 rounded-lg cursor-pointer hover:bg-surface-200 transition-colors min-w-[140px]"
                    >
                      <option value="">Sin categoría</option>
                      <option value="politics">Política</option>
                      <option value="society">Sociedad</option>
                      <option value="economy">Economía</option>
                      <option value="culture">Cultura</option>
                      <option value="entertainment">Entretenimiento</option>
                      <option value="sports">Deportes</option>
                      <option value="technology">Tecnología</option>
                      <option value="science">Ciencia</option>
                      <option value="energy">Energía</option>
                      <option value="lifestyle">Lifestyle</option>
                      <option value="unclassified">Inclasificables</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={() => handleDetailedAnalysisToggle(statistic)}
                    disabled={togglingDetailedAnalysis === statistic.id}
                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                      isDetailedAnalysisVisible(statistic.data)
                        ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    title={
                      isDetailedAnalysisVisible(statistic.data)
                        ? 'Análisis Detallado visible en artículo — clic para ocultar'
                        : 'Análisis Detallado oculto en artículo — clic para mostrar'
                    }
                  >
                    <FileText size={16} />
                  </button>

                  <button
                    onClick={() => handleFeaturedChange(statistic)}
                    className={`p-2 rounded-lg transition-colors ${
                      statistic.featured 
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={statistic.featured ? 'Quitar de destacados' : 'Marcar como destacado'}
                  >
                    <Star size={16} />
                  </button>
                  
                  <button
                    onClick={() => handleClearCache(statistic.id, statistic.title)}
                    disabled={clearingCache === statistic.id}
                    className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Limpiar cache"
                  >
                    {clearingCache === statistic.id ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleDelete(statistic.id)}
                    className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Featured Confirmation Modal */}
      {showFeaturedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowFeaturedModal(false)}
          />
          
          <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 text-yellow-600 rounded-lg">
                  <Star size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    Confirmar Cambio de Artículo Destacado
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Solo puede haber un artículo destacado a la vez
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFeaturedModal(false)}
                className="p-2 hover:bg-surface-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {currentFeaturedStatistic && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">Artículo que se va a quitar de destacados:</h4>
                  <div className="text-sm text-red-700">
                    <p className="font-medium">{currentFeaturedStatistic.title}</p>
                    <p className="text-red-600">Categoría: {currentFeaturedStatistic.category ? getCategoryDisplayName(currentFeaturedStatistic.category) : 'Sin categoría'}</p>
                  </div>
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">Artículo que se va a marcar como destacado:</h4>
                <div className="text-sm text-green-700">
                  <p className="font-medium">{featuredStatistic?.title}</p>
                                      <p className="text-green-600">Categoría: {featuredStatistic?.category ? getCategoryDisplayName(featuredStatistic.category) : 'Sin categoría'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 pt-4">
                <button
                  onClick={() => setShowFeaturedModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => updateFeaturedStatus(featuredStatistic!.id, true)}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Star size={16} />
                  Confirmar Cambio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create New Statistic Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseModal}
          />
          
          <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                  <Sparkles size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {currentStep === 'topic' && 'Paso 1: Tema de la Estadística'}
                    {currentStep === 'blocks' && 'Paso 2: Bloques de Cálculo'}
                    {currentStep === 'segments' && 'Paso 3: Segmentaciones'}
                    {currentStep === 'info' && 'Paso 4: Información General'}
                    {currentStep === 'preview' && 'Vista Previa Final'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {currentStep === 'topic' && 'Describe el tema y el asistente generará los bloques de cálculo'}
                    {currentStep === 'blocks' && 'Revisa y edita los bloques de cálculo generados'}
                    {currentStep === 'segments' && 'Revisa y edita las segmentaciones generadas'}
                    {currentStep === 'info' && 'Revisa la información general de la estadística'}
                    {currentStep === 'preview' && 'Revisa todos los datos antes de guardar'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

                        <div className="p-6 space-y-6">
              {currentStep === 'topic' && (
                <>
                  {/* Topic Field */}
                  <div>
                    <label htmlFor="topic" className="block text-sm font-medium text-foreground mb-2">
                      Describe el tema de la estadística *
                    </label>
                    <textarea
                      id="topic"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="Ej: ¿Cuántas personas se bañan al día en una piscina en España? Incluye datos del verano, segmentación por edad y género, y fuentes oficiales."
                      className="form-textarea w-full h-24 resize-none"
                      disabled={isCreating}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      El asistente generará los bloques de cálculo basándose en este tema
                    </p>
                  </div>

                  {/* Preview Info */}
                  <div className="bg-surface-100 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-2">Proceso de 3 pasos:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• <strong>Paso 1:</strong> Generar bloques de cálculo</li>
                      <li>• <strong>Paso 2:</strong> Generar segmentaciones</li>
                      <li>• <strong>Paso 3:</strong> Generar información general</li>
                    </ul>
                  </div>
                </>
              )}

              {currentStep === 'blocks' && (
                <>
                  {/* Blocks Review and Edit */}
                  <div>
                    <h4 className="font-medium text-foreground mb-4">Bloques de Cálculo Generados</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Revisa y edita los bloques antes de continuar. Los cambios se guardarán automáticamente.
                    </p>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {blocksData.map((block, index) => (
                        <div key={index} className="bg-surface-100 rounded-lg p-4 border border-surface-200">
                          {editingBlockIndex === index ? (
                            // Edit mode
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h5 className="font-medium text-foreground">Editando: {block.id}</h5>
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleSaveBlock}
                                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    onClick={handleCancelEditBlock}
                                    className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Título</label>
                                <input
                                  type="text"
                                  value={editingBlockData?.title || ''}
                                  onChange={(e) => handleUpdateBlockField('title', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                />
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1">Valor</label>
                                  <input
                                    type="number"
                                    value={editingBlockData?.value || ''}
                                    onChange={(e) => handleUpdateBlockField('value', parseFloat(e.target.value) || 0)}
                                    className="w-full p-2 border border-border rounded text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1">Unidad</label>
                                  <input
                                    type="text"
                                    value={editingBlockData?.unit || ''}
                                    onChange={(e) => handleUpdateBlockField('unit', e.target.value)}
                                    className="w-full p-2 border border-border rounded text-sm"
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
                                <select
                                  value={editingBlockData?.type || 'extracted'}
                                  onChange={(e) => handleUpdateBlockField('type', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                >
                                  <option value="extracted">Extraído</option>
                                  <option value="calculated">Calculado</option>
                                  <option value="assumption">Suposición</option>
                                  <option value="estimation">Estimación</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
                                <textarea
                                  value={editingBlockData?.description || ''}
                                  onChange={(e) => handleUpdateBlockField('description', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                  rows={3}
                                />
                              </div>
                              
                              {editingBlockData?.formula && (
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1">Fórmula</label>
                                  <input
                                    type="text"
                                    value={editingBlockData?.formula || ''}
                                    onChange={(e) => handleUpdateBlockField('formula', e.target.value)}
                                    className="w-full p-2 border border-border rounded text-sm"
                                  />
                                </div>
                              )}
                              
                              {editingBlockData?.source && (
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1">Fuente</label>
                                  <input
                                    type="text"
                                    value={editingBlockData?.source || ''}
                                    onChange={(e) => handleUpdateBlockField('source', e.target.value)}
                                    className="w-full p-2 border border-border rounded text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            // View mode
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-foreground">{block.title}</h5>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">{block.type}</span>
                                  <button
                                    onClick={() => handleEditBlock(index)}
                                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                  >
                                    Editar
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Valor:</span>
                                  <span className="ml-2 font-medium">{block.value?.toLocaleString()}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Unidad:</span>
                                  <span className="ml-2">{block.unit}</span>
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground mt-2">{block.description}</p>
                              {block.formula && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  <strong>Fórmula:</strong> {block.formula}
                                </p>
                              )}
                              {block.source && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  <strong>Fuente:</strong> {block.source}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {currentStep === 'segments' && (
                <>
                  {/* Segments Review and Edit */}
                  <div>
                    <h4 className="font-medium text-foreground mb-4">Segmentaciones Generadas</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Revisa y edita las segmentaciones antes de continuar. Los cambios se guardarán automáticamente.
                    </p>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {segmentsData.map((segment, index) => (
                        <div key={index} className="bg-surface-100 rounded-lg p-4 border border-surface-200">
                          {editingSegmentIndex === index ? (
                            // Edit mode
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h5 className="font-medium text-foreground">Editando: {segment.dimension_title || segment.dimension}</h5>
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleSaveSegment}
                                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    onClick={handleCancelEditSegment}
                                    className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Título de Dimensión</label>
                                <input
                                  type="text"
                                  value={editingSegmentData?.dimension_title || ''}
                                  onChange={(e) => handleUpdateSegmentField('dimension_title', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Dimensión</label>
                                <input
                                  type="text"
                                  value={editingSegmentData?.dimension || ''}
                                  onChange={(e) => handleUpdateSegmentField('dimension', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Bloque Objetivo</label>
                                <input
                                  type="text"
                                  value={editingSegmentData?.target_block || ''}
                                  onChange={(e) => handleUpdateSegmentField('target_block', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
                                <textarea
                                  value={editingSegmentData?.description || ''}
                                  onChange={(e) => handleUpdateSegmentField('description', e.target.value)}
                                  className="w-full p-2 border border-border rounded text-sm"
                                  rows={3}
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Valores (%)</label>
                                <div className="space-y-2">
                                  {editingSegmentData?.values && Object.entries(editingSegmentData.values).map(([key, value]) => (
                                    <div key={key} className="flex items-center gap-2">
                                      <span className="text-sm text-muted-foreground min-w-[80px]">{key}:</span>
                                      <input
                                        type="number"
                                        value={value as number}
                                        onChange={(e) => handleUpdateSegmentValue(key, parseFloat(e.target.value) || 0)}
                                        className="flex-1 p-2 border border-border rounded text-sm"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                      />
                                      <span className="text-sm text-muted-foreground">%</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // View mode
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-foreground">{segment.dimension_title || segment.dimension}</h5>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">Bloque: {segment.target_block}</span>
                                  <button
                                    onClick={() => handleEditSegment(index)}
                                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                  >
                                    Editar
                                  </button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                                {Object.entries(segment.values).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="text-muted-foreground">{key}:</span>
                                    <span className="font-medium">{String(value)}%</span>
                                  </div>
                                ))}
                              </div>
                              <p className="text-sm text-muted-foreground">{segment.description}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {currentStep === 'info' && infoData && (
                <>
                  {/* Info Review */}
                  <div>
                    <h4 className="font-medium text-foreground mb-4">Información General</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Título</label>
                        <p className="text-sm text-muted-foreground">{infoData.title}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{infoData.description}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Tags</label>
                        <div className="flex flex-wrap gap-1">
                          {infoData.tags?.map((tag: string, index: number) => (
                            <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Categoría</label>
                        <p className="text-sm text-muted-foreground">
                          {infoData.category ? 
                            infoData.category.charAt(0).toUpperCase() + infoData.category.slice(1) : 
                            'No especificada'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {currentStep === 'preview' && generatedData && (
                <>
                  {/* Generated Data Preview */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                        <Sparkles size={20} />
                      </div>
                      <h4 className="text-lg font-semibold text-foreground">Vista Previa - Datos Generados</h4>
                    </div>

                    {/* Basic Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Título</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.title || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Lead</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.lead || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Autor</label>
                        <select
                          value={generatedData?.authorId || ''}
                          onChange={(e) => {
                            const selected = authors.find((a) => a.id === e.target.value);
                            setGeneratedData((prev: any) => ({
                              ...prev,
                              authorId: selected?.id,
                              author: selected?.name || prev?.author,
                            }));
                          }}
                          className="w-full p-3 bg-surface-100 rounded-lg text-sm border border-surface-300"
                        >
                          {authors.length === 0 && (
                            <option value="">{generatedData?.author || 'Equipo de Statemia'}</option>
                          )}
                          {authors.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}{a.isDefault ? ' (por defecto)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Ubicación</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.location || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Idioma</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.language || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Flujo de Estudio</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.studyFlow || generatedData?.study_flow || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Categoría</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">
                          {generatedData?.category ? 
                            generatedData.category.charAt(0).toUpperCase() + generatedData.category.slice(1) : 
                            'No especificada'
                          }
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
                      <div className="p-3 bg-surface-100 rounded-lg text-sm max-h-32 overflow-y-auto">{generatedData?.description || 'No generado'}</div>
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Tags</label>
                      <div className="flex flex-wrap gap-2">
                        {generatedData?.tags?.map((tag: string, index: number) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {tag}
                          </span>
                        )) || <span className="text-muted-foreground text-sm">No generados</span>}
                      </div>
                    </div>

                    {/* SEO Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Meta Título</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.metaTitle || generatedData?.meta_title || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Meta Descripción</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.metaDescription || generatedData?.meta_description || 'No generado'}</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Keywords</label>
                        <div className="flex flex-wrap gap-1">
                          {(generatedData?.keywords || generatedData?.seo?.keywords)?.map((keyword: string, index: number) => (
                            <span key={index} className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                              {keyword}
                            </span>
                          )) || <span className="text-muted-foreground text-sm">No generados</span>}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Productos de Afiliación</label>
                        <div className="flex flex-wrap gap-1">
                          {generatedData?.relatedAdProducts?.map((product: string, index: number) => (
                            <span key={index} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                              {product}
                            </span>
                          )) || <span className="text-muted-foreground text-sm">No generados</span>}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">URL Canónica</label>
                        <div className="p-3 bg-surface-100 rounded-lg text-sm">{generatedData?.canonicalUrl || generatedData?.canonical_url || 'No generado'}</div>
                      </div>
                    </div>

                    {/* JSONB Data */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Datos JSONB (Bloques, Segmentaciones, Evolución)</label>
                      <div className="p-3 bg-surface-100 rounded-lg text-xs max-h-48 overflow-y-auto">
                        <pre className="whitespace-pre-wrap">{JSON.stringify(generatedData?.data, null, 2)}</pre>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              {currentStep === 'topic' && (
                <>
                  <button
                    onClick={handleCloseModal}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleGenerateBlocks}
                    className="btn-primary flex items-center gap-2"
                    disabled={isCreating || !topic.trim()}
                  >
                    {isCreating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Generando bloques...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Generar Bloques
                      </>
                    )}
                  </button>
                </>
              )}

              {currentStep === 'blocks' && (
                <>
                  <button
                    onClick={() => setCurrentStep('topic')}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Volver
                  </button>
                  <button
                    onClick={handleGenerateSegments}
                    className="btn-primary flex items-center gap-2"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Generando segmentos...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Generar Segmentos
                      </>
                    )}
                  </button>
                </>
              )}

              {currentStep === 'segments' && (
                <>
                  <button
                    onClick={() => setCurrentStep('blocks')}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Volver
                  </button>
                  <button
                    onClick={handleGenerateInfo}
                    className="btn-primary flex items-center gap-2"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Generando información...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Generar Información
                      </>
                    )}
                  </button>
                </>
              )}

              {currentStep === 'info' && (
                <>
                  <button
                    onClick={() => setCurrentStep('segments')}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Volver
                  </button>
                  <button
                    onClick={() => {
                      setCurrentStep('preview');
                      setShowPreview(true);
                    }}
                    className="btn-primary flex items-center gap-2"
                    disabled={isCreating}
                  >
                    Ver Vista Previa
                  </button>
                </>
              )}

              {currentStep === 'preview' && (
                <>
                  <button
                    onClick={() => setCurrentStep('info')}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Volver
                  </button>
                  <button
                    onClick={handleCreateFromPreview}
                    className="btn-primary flex items-center gap-2"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Creando...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Crear Estadística
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Curioseme Modal */}
      {showCuriosemeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseCuriosemeModal}
          />
          
          <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                  <Sparkles size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {!showPreview ? 'Crear Nueva Curioseme' : 'Vista Previa del Curioseme'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {!showPreview ? 'Genera un curioseme basado en estadísticas existentes' : 'Revisa el contenido antes de guardar'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseCuriosemeModal}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {!showPreview ? (
                <>
                  {/* Topic Field */}
                  <div>
                    <label htmlFor="curioseme-topic" className="block text-sm font-medium text-foreground mb-2">
                      Describe el tema del curioseme *
                    </label>
                    <textarea
                      id="curioseme-topic"
                      value={curiosemeTopic}
                      onChange={(e) => setCuriosemeTopic(e.target.value)}
                      placeholder="Ej: ¿Qué pasaría si combinamos los datos de consumo de agua en duchas y piscinas? Crea una curiosidad interesante sobre el impacto ambiental."
                      className="form-textarea w-full h-24 resize-none"
                      disabled={isGeneratingCurioseme}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      El asistente generará un curioseme basándose en este tema y las estadísticas seleccionadas
                    </p>
                  </div>

                  {/* Related Statistics Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Estadísticas Relacionadas (opcional, máximo 2)
                    </label>
                    <div className="space-y-3">
                      {/* Selected Statistics */}
                      {relatedStatistics.length > 0 && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Estadísticas seleccionadas:</h4>
                          <div className="space-y-2">
                            {relatedStatistics.map((stat, index) => (
                              <div key={index} className="flex items-center justify-between bg-white rounded p-3 border">
                                <div>
                                  <h5 className="font-medium text-foreground">{stat.title}</h5>
                                  <p className="text-sm text-muted-foreground">{stat.location}</p>
                                </div>
                                <button
                                  onClick={() => handleRemoveRelatedStatistic(index)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Available Statistics Dropdown */}
                      {relatedStatistics.length < 2 && (
                        <div>
                          <select
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              if (selectedId) {
                                const selectedStat = availableStatistics.find(s => s.id === selectedId);
                                if (selectedStat) {
                                  handleAddRelatedStatistic(selectedStat);
                                }
                                e.target.value = '';
                              }
                            }}
                            className="form-input"
                            defaultValue=""
                          >
                            <option value="">Seleccionar estadística relacionada...</option>
                            {availableStatistics
                              .filter(stat => !relatedStatistics.find(rs => rs.id === stat.id))
                              .map(stat => (
                                <option key={stat.id} value={stat.id}>
                                  {stat.title} ({stat.location})
                                </option>
                              ))
                            }
                          </select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Selecciona hasta 2 estadísticas para crear el curioseme
                          </p>
                        </div>
                      )}
                    </div>
                  </div>



                  {/* Generate Button */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleCloseCuriosemeModal}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleGenerateCurioseme}
                      className="btn-primary flex items-center gap-2"
                      disabled={!curiosemeTopic.trim() || isGeneratingCurioseme}
                    >
                      {isGeneratingCurioseme ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Generando...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} />
                          Generar Curioseme
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Preview Content */}
                  {generatedCurioseme && (
                    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Título:</h4>
                        <p className="text-foreground">{generatedCurioseme.title}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Lead:</h4>
                        <p className="text-foreground">{generatedCurioseme.lead}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Descripción:</h4>
                        <p className="text-foreground whitespace-pre-line">{generatedCurioseme.description}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Flujo de estudio:</h4>
                        <p className="text-foreground whitespace-pre-line">{generatedCurioseme.studyFlow}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Ubicación:</h4>
                        <p className="text-foreground">{generatedCurioseme.location}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Idioma:</h4>
                        <p className="text-foreground">{generatedCurioseme.language}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Autor:</h4>
                        <p className="text-foreground">{generatedCurioseme.author}</p>
                      </div>

                      {generatedCurioseme.tags && generatedCurioseme.tags.length > 0 && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Tags:</h4>
                          <div className="flex flex-wrap gap-2">
                            {generatedCurioseme.tags.map((tag: string, index: number) => (
                              <span key={index} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {generatedCurioseme.keywords && generatedCurioseme.keywords.length > 0 && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Keywords:</h4>
                          <div className="flex flex-wrap gap-2">
                            {generatedCurioseme.keywords.map((keyword: string, index: number) => (
                              <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Meta título:</h4>
                        <p className="text-foreground">{generatedCurioseme.metaTitle}</p>
                      </div>

                      <div className="bg-surface-100 rounded-lg p-4">
                        <h4 className="font-medium text-foreground mb-2">Meta descripción:</h4>
                        <p className="text-foreground">{generatedCurioseme.metaDescription}</p>
                      </div>

                      {generatedCurioseme.relatedLinks && generatedCurioseme.relatedLinks.length > 0 && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Enlaces relacionados:</h4>
                          <ul className="list-disc list-inside text-foreground">
                            {generatedCurioseme.relatedLinks.map((link: string, index: number) => (
                              <li key={index}>{link}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {generatedCurioseme.relatedAdProducts && generatedCurioseme.relatedAdProducts.length > 0 && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Productos de afiliación:</h4>
                          <ul className="list-disc list-inside text-foreground">
                            {generatedCurioseme.relatedAdProducts.map((product: string, index: number) => (
                              <li key={index}>{product}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {generatedCurioseme.data && generatedCurioseme.data.blocks && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Bloques de datos ({generatedCurioseme.data.blocks.length}):</h4>
                          <div className="space-y-2">
                            {generatedCurioseme.data.blocks.map((block: any, index: number) => (
                              <div key={index} className="bg-white rounded p-2 border">
                                <div className="font-medium text-sm">{block.id}: {block.title}</div>
                                <div className="text-xs text-muted-foreground">Tipo: {block.type} | Valor: {block.value} {block.unit}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {generatedCurioseme.data && generatedCurioseme.data.segments && (
                        <div className="bg-surface-100 rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-2">Segmentos ({generatedCurioseme.data.segments.length}):</h4>
                          <div className="space-y-2">
                            {generatedCurioseme.data.segments.map((segment: any, index: number) => (
                              <div key={index} className="bg-white rounded p-2 border">
                                <div className="font-medium text-sm">{segment.dimension_title || segment.dimension}</div>
                                <div className="text-xs text-muted-foreground">Bloque objetivo: {segment.target_block}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                          onClick={() => setShowPreview(false)}
                          className="btn-secondary"
                        >
                          Volver
                        </button>
                        <button
                          onClick={handleSaveCurioseme}
                          className="btn-primary flex items-center gap-2"
                        >
                          <Sparkles size={16} />
                          Guardar Curioseme
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Statistics Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowImportModal(false)}
          />
          
          <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <FileText size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">Importar Estadísticas</h3>
                  <p className="text-sm text-muted-foreground">Sube un archivo JSON para importar estadísticas existentes.</p>
                </div>
              </div>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label htmlFor="import-file" className="block text-sm font-medium text-foreground mb-2">
                  Selecciona un archivo JSON para importar
                </label>
                <input
                  type="file"
                  id="import-file"
                  accept=".json"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setImportFile(e.target.files[0]);
                      setImportError('');
                    }
                  }}
                  className="block w-full text-sm text-foreground border border-surface-300 rounded-lg cursor-pointer bg-surface-100 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                />
                {importFile && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Archivo seleccionado: {importFile.name}
                  </p>
                )}
                {importError && (
                  <p className="text-sm text-red-500 mt-2">{importError}</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImportStatistics}
                  className="btn-primary flex items-center gap-2"
                  disabled={!importFile || isImporting}
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importando...
                    </>
                  ) : (
                    <>
                      <FileText size={16} />
                      Importar Estadísticas
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Statistic Modal */}
      <NewStatisticModal
        isOpen={showNewStatisticModal}
        onClose={() => setShowNewStatisticModal(false)}
      />
    </div>
  );
};

export default Statistics; 